import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

export default class extends Controller {
  static targets = ["select", "statusRadios", "faceCheckboxes"]
  static values = {
    url: String,
    treatmentInfoUrl: String,
    dentalServiceInfoUrl: String,
    allowStatusChange: Boolean,
    hasExistingRequiredTreatment: Boolean
  }

  toggleAllFaces(e) {
    if (!this.hasFaceCheckboxesTarget) return
    const checked = !!e.target.checked
    const checkboxes = this.faceCheckboxesTarget.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = checked
    })
  }

  connect() {
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this)
    this.element.addEventListener('submit', this.boundHandleFormSubmit)
    this.initializePresetTreatment()
  }

  disconnect() {
    if (this.boundHandleFormSubmit) {
      this.element.removeEventListener('submit', this.boundHandleFormSubmit)
    }
  }

  handleFormSubmit(e) {
    if (this.hasFaceCheckboxesTarget) {
      const checkboxes = this.faceCheckboxesTarget.querySelectorAll('input[type="checkbox"]')
      checkboxes.forEach(checkbox => {
        if (checkbox.readOnly) {
          checkbox.readOnly = false
        }
      })
    }
  }

  async openModal(e) {
    const treatment_type = this.selectTarget.value

    if (!treatment_type) return

    if (this.dentalServiceInfoUrlValue) {
      try {
        const response = await get(this.dentalServiceInfoUrlValue, {
          responseKind: "json",
          query: { dental_service_id: treatment_type }
        })

        if (response.ok) {
          const data = await response.json
          
          if (data.kind === "general") {
            return
          }
        }
      } catch (error) {
        console.error("Error fetching dental service info:", error)
      }
    }

    if (this.urlValue && treatment_type) {
      get(this.urlValue, {
        responseKind: "turbo-stream",
        query: {
          treatment_type: treatment_type
        }
      })
    }
  }

  async handleTreatmentChange(e) {
    const treatment_type = this.selectTarget.value
    
    if (this.treatmentInfoUrlValue && treatment_type) {
      try {
        const response = await get(this.treatmentInfoUrlValue, {
          responseKind: "json",
          query: { treatment_type: treatment_type }
        })
        
        if (response.ok) {
          const data = await response.json
          this.applyTreatmentRestrictions(data)
        }
      } catch (error) {
        console.error("Error fetching treatment info:", error)
      }
    }
  }

  applyTreatmentRestrictions(treatmentData) {
    const presetStatus = treatmentData.status_preset
    const isFullCrownTreatment = this.isFullCrownTreatment(treatmentData)

    if (this.hasFaceCheckboxesTarget) {
      const checkboxes = this.faceCheckboxesTarget.querySelectorAll('input[type="checkbox"]')
      
      if (isFullCrownTreatment) {
        checkboxes.forEach(checkbox => {
          checkbox.checked = true
          checkbox.readOnly = true
          checkbox.onclick = (e) => {
            e.preventDefault()
            return false
          }
        })
      } else {
        checkboxes.forEach(checkbox => {
          checkbox.readOnly = false
          checkbox.onclick = null
        })
      }
    }
  
    if (!this.hasStatusRadiosTarget) return
    const radios = this.statusRadiosTarget.querySelectorAll('input[type="radio"]')
  
    radios.forEach(radio => {
      radio.disabled = false
      radio.style.pointerEvents = ''
      radio.style.opacity = ''
      radio.onclick = null
    })

    if (presetStatus === "required" && this.allowStatusChangeValue && this.hasExistingRequiredTreatmentValue) {
      const radioToCheck = this.statusRadiosTarget.querySelector('input[value="existente"]')
      if (radioToCheck) radioToCheck.checked = true
      
      radios.forEach(radio => {
        if (radio.value === "requerido") {
          radio.style.pointerEvents = 'none'
          radio.style.opacity = '0.5'
          radio.onclick = e => e.preventDefault()
        }
      })
    } else if (presetStatus === "required" && this.allowStatusChangeValue && !this.hasExistingRequiredTreatmentValue) {
      const radioToCheck = this.statusRadiosTarget.querySelector('input[value="requerido"]')
      if (radioToCheck) radioToCheck.checked = true

      radios.forEach(radio => {
        radio.style.pointerEvents = 'none'
        radio.style.opacity = '0.5'
        radio.onclick = e => e.preventDefault()
      })
    } else if ((presetStatus === "required" || presetStatus === "existing") && !this.allowStatusChangeValue) {
      const radioToCheck = this.statusRadiosTarget.querySelector(`input[value="${presetStatus === 'required' ? 'requerido' : 'existente'}"]`)
      if (radioToCheck) radioToCheck.checked = true

      radios.forEach(radio => {
        radio.style.pointerEvents = 'none'
        radio.style.opacity = '0.5'
        radio.onclick = e => e.preventDefault()
      })
    } else if (presetStatus === "existing" && this.allowStatusChangeValue) {
      const radioToCheck = this.statusRadiosTarget.querySelector('input[value="existente"]')
      if (radioToCheck) radioToCheck.checked = true
      
      radios.forEach(radio => {
        radio.style.pointerEvents = 'none'
        radio.style.opacity = '0.5'
        radio.onclick = e => e.preventDefault()
      })
    } else if (presetStatus === "existing") {
      const radioToCheck = this.statusRadiosTarget.querySelector('input[value="existente"]')
      if (radioToCheck) radioToCheck.checked = true
    }
  }

  normalizeName(name) {
    return (name || "")
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
  }

  isFullCrownTreatment(treatmentData) {
    if (typeof treatmentData.full_crown_symbol === "boolean") {
      return treatmentData.full_crown_symbol
    }

    const fullCrownByCode = new Set(['ausente', 'extraccion', 'corona'])
    if (fullCrownByCode.has(treatmentData.symbol_code)) {
      return true
    }

    // fallback codigo legacy
    const treatmentNameNormalized = this.normalizeName(treatmentData.name)
    return fullCrownByCode.has(treatmentNameNormalized)
  }

  removeRestrictions() {
    if (this.hasFaceCheckboxesTarget) {
      const checkboxes = this.faceCheckboxesTarget.querySelectorAll('input[type="checkbox"]')
      checkboxes.forEach(checkbox => {
        checkbox.readOnly = false
        checkbox.onclick = null
      })
    }

    if (this.hasStatusRadiosTarget) {
      const radios = this.statusRadiosTarget.querySelectorAll('input[type="radio"]')
      radios.forEach(radio => {
        radio.disabled = false
        radio.style.pointerEvents = ''
        radio.style.opacity = ''
        radio.onclick = null
      })
    }
  }

  async initializePresetTreatment() {
    setTimeout(async () => {
      const hiddenField = this.element.querySelector('input[name*="dental_service_id"][type="hidden"]')
      if (hiddenField && hiddenField.value && this.treatmentInfoUrlValue) {
        try {
          const response = await get(this.treatmentInfoUrlValue, {
            responseKind: "json",
            query: { treatment_type: hiddenField.value }
          })
          
          if (response.ok) {
            const data = await response.json
            this.applyTreatmentRestrictions(data)
          }
        } catch (error) {
          console.error("Error fetching preset treatment info:", error)
        }
      }
    }, 100)
  }

  getPresetTreatmentName() {
    const readonlyInput = this.element.querySelector('input[readonly]')
    if (readonlyInput && readonlyInput.value) {
      return readonlyInput.value
    }
    
    const label = this.element.querySelector('label')
    if (label && label.textContent) {
      return label.textContent.trim()
    }
    
    return null
  }

  getPresetStatus() {
    const checkedRadio = this.statusRadiosTarget?.querySelector('input[type="radio"]:checked')
    if (checkedRadio) {
      return checkedRadio.value === 'requerido' ? 'required' : 'existing'
    }
    return null
  }
}